import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Confirmation, Icon, StackList, StackListItem, StackListValue, Tabs, Title } from 'admination-react'
import { useApp } from '@/core/state/appStore'
import type { DokumentBlob } from '@/core/hooks/useDokumentBlob'
import { DropCard } from '@/core/components/modal/DropCard'
import { ModalHeader, type TabOverrides } from '@/core/components/modal/ModalHeader'
import { PdfRegion } from '@/core/components/modal/PdfRegion'
import {
  GEVA_ABSCHNITTE,
  GEVA_KLETT_ACCEPT,
  KLETT_BEREICHE,
  type GevaDossier,
  type GevaMerkmal,
  type GevaKlettStatus,
  type GevaUploadResult,
  type KlettUploadResult,
} from '../../api/gevaKlett'

export type GevaAnsicht = 'dokument' | 'ausgelesen'
type KlettStand = Pick<KlettUploadResult, 'niveau' | 'punkte' | 'punkte_max' | 'bereiche'>

type TestProps = {
  tabs: TabOverrides
  pdf: DokumentBlob
  statusLoading: boolean
  statusError: boolean
  loading: boolean
  ansicht: GevaAnsicht
  onAnsicht: (ansicht: GevaAnsicht) => void
  onFile: (file: File) => void
  onWeiter: () => void
} & (
  | { step: 'geva'; stand: GevaKlettStatus['geva']; result: GevaUploadResult | null }
  | { step: 'klett'; stand: GevaKlettStatus['klett']; result: KlettUploadResult | null }
)

export function GevaHochladen(props: TestProps) {
  const { step, stand, tabs, pdf, statusLoading, statusError, loading, ansicht, onAnsicht, onFile, onWeiter } = props
  const { dispatch } = useApp()
  const { t } = useTranslation('vergabeStaff')
  const done = stand.fertig
  const dokumentId = stand.dokument_id
  const pruefen = !!props.result && !statusError
  const mitDokument = !statusError && (!!props.result || done) && !!dokumentId
  let ausgelesen = null
  if (props.step === 'geva') {
    const dossier = props.result?.dossier ?? (done ? props.stand.dossier : null)
    if (dossier) ausgelesen = <GevaDossierListe dossier={dossier} />
  } else {
    const { niveau, punkte, punkte_max, bereiche } = props.stand
    const werte = props.result ?? (done && niveau !== null && punkte !== null && punkte_max !== null && bereiche
      ? { niveau, punkte, punkte_max, bereiche } : null)
    if (werte) ausgelesen = <KlettListe werte={werte} />
  }
  return (
    <>
      <ModalHeader
        size={mitDokument && ansicht === 'dokument' ? 'document' : 'form'}
        tabs={tabs}
        center={
          mitDokument && (
            <Tabs
              items={[
                { name: 'dokument', label: t(`gevaKlett.${step}.dokument`), icon: 'i-doc' },
                { name: 'ausgelesen', label: t('gevaKlett.ausgelesen'), icon: 'i-list' },
              ]}
              activeName={ansicht}
              onChange={(name) => onAnsicht(name === 'ausgelesen' ? 'ausgelesen' : 'dokument')}
            />
          )
        }
        onClose={() => dispatch({ type: 'CLOSE' })}
      >
        <div className="pill-cluster">
          {pruefen && (
            <div className="pill">
              <Button primaryAction iconOnly title={t('gevaKlett.weiter')} onClick={onWeiter}>
                <Icon name="i-check" />
              </Button>
            </div>
          )}
        </div>
      </ModalHeader>
      {statusError ? (
        <Confirmation tone="error" title={t('gevaKlett.statusErrorTitle')}>
          {t('gevaKlett.statusErrorText')}
        </Confirmation>
      ) : mitDokument && ansicht === 'dokument' ? (
        <PdfRegion
          testId="geva-klett-dokument"
          path={null}
          localBlob={pdf.blob}
          waiting={pdf.laedt}
          loadingLabel={t(`gevaKlett.${step}.dokumentLaedt`)}
          placeholder={t(`gevaKlett.${step}.dokumentError`)}
        />
      ) : ausgelesen ?? (done ? (
        <Confirmation tone="success" title={t(`gevaKlett.${step}.doneTitle`)}>
          {t(`gevaKlett.${step}.doneText`)}
        </Confirmation>
      ) : (
        <DropCard
          title={t(`gevaKlett.${step}.dropTitle`)}
          body={t(`gevaKlett.${step}.dropBody`)}
          accept={GEVA_KLETT_ACCEPT}
          onFile={onFile}
          loading={loading || statusLoading}
          loadingLabel={loading ? t(`gevaKlett.${step}.reading`) : t('gevaKlett.statusLoading')}
        />
      ))}
    </>
  )
}

function KlettListe({ werte }: { werte: KlettStand }) {
  const { t, i18n } = useTranslation('vergabeStaff')
  const zahl = (n: number) => new Intl.NumberFormat(i18n.language).format(n)
  const punkte = (p: number, max: number) => t('gevaKlett.klett.punkte', { punkte: zahl(p), max: zahl(max) })
  return (
    <main className="form" data-scroll-host>
      <Title as="h3">{t('gevaKlett.klett.resultTitle')}</Title>
      <StackList>
        <StackListItem end={<StackListValue>{werte.niveau}</StackListValue>}>
          {t('gevaKlett.klett.niveau')}
        </StackListItem>
        <StackListItem end={<StackListValue>{punkte(werte.punkte, werte.punkte_max)}</StackListValue>}>
          {t('gevaKlett.klett.gesamt')}
        </StackListItem>
        {KLETT_BEREICHE.map((key) => (
          <StackListItem
            key={key}
            end={<StackListValue>{punkte(werte.bereiche[key].punkte, werte.bereiche[key].punkte_max)}</StackListValue>}
          >
            {t(`gevaKlett.klett.bereiche.${key}`)}
          </StackListItem>
        ))}
      </StackList>
    </main>
  )
}

function GevaDossierListe({ dossier }: { dossier: GevaDossier }) {
  const { t, i18n } = useTranslation('vergabeStaff')
  const prozent = (n: number | null) =>
    n === null
      ? t('gevaKlett.geva.keinWert')
      : new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 }).format(n / 100)
  const zeile = (m: GevaMerkmal, key: number) => (
    <StackListItem
      key={key}
      end={
        <StackListValue>
          {t('gevaKlett.geva.wert', { wert: prozent(m.wert), vergleich: prozent(m.vergleichsgruppe) })}
        </StackListValue>
      }
    >
      {m.merkmal}
    </StackListItem>
  )
  const kopf = [
    ['name', dossier.name],
    ['teilnehmernummer', dossier.teilnehmernummer],
    ['geburtsdatum', dossier.geburtsdatum],
    ['anmerkungen', dossier.anmerkungen],
  ] as const

  return (
    <main className="form" data-scroll-host>
      <Title as="h3">{t('gevaKlett.geva.resultTitle')}</Title>
      <StackList>
        {kopf.map(([key, wert]) => (
          <StackListItem key={key} end={<StackListValue>{wert || t('gevaKlett.geva.keinWert')}</StackListValue>}>
            {t(`gevaKlett.geva.felder.${key}`)}
          </StackListItem>
        ))}
      </StackList>
      {GEVA_ABSCHNITTE.map((abschnitt) => (
        <Fragment key={abschnitt}>
          <Title as="h4">{t(`gevaKlett.geva.abschnitte.${abschnitt}`)}</Title>
          {gruppiert(dossier[abschnitt]).map(({ gruppe, merkmale }, i) => (
            <Fragment key={i}>
              {gruppe && <Title as="h5">{gruppe}</Title>}
              <StackList>{merkmale.map(zeile)}</StackList>
            </Fragment>
          ))}
        </Fragment>
      ))}
    </main>
  )
}

function gruppiert(merkmale: GevaMerkmal[]): { gruppe: string; merkmale: GevaMerkmal[] }[] {
  const gruppen: { gruppe: string; merkmale: GevaMerkmal[] }[] = []
  for (const m of merkmale) {
    const letzte = gruppen.at(-1)
    if (letzte && letzte.gruppe === m.gruppe) letzte.merkmale.push(m)
    else gruppen.push({ gruppe: m.gruppe, merkmale: [m] })
  }
  return gruppen
}
